import { inApp, isBrowser } from '@/modules/detector';
import {
  isArray,
  isString,
  isInvalid,
  isFunction,
  isUndefined,
  isObject,
} from '@/modules/validate';
import { loggerInfo } from '@/modules/logger';
import { callHandlerToNative, checkSidJsBridge, ICheckSidJsBridge } from '@/modules/jsBridge';
import { storage } from '@/modules/storage';
import { Platform } from '@/modules/url/common';
import { getUtilsConfig } from '@/modules/config';
import { LogModule } from '@/data/config';
import {
  JsBridge,
  IHtmlSpreadInterceptOpts,
  THtmlSpreadInterceptRule,
  IScrollToViewAnimateOptions,
} from '@/interface';
import { getCookie } from '../cookie';
import { HsUtilPreloadImages } from '@/data/storage';
import { calcCssUnit, trimCssUnit } from './style';

export * from './style';
export * from './download';
export * from './image';

/**
 * stopPropagation 阻止冒泡行为
 * @param {Event} e
 */
export function stopPropagation(e: Event): void {
  const event = e || window.event;
  if (event && event.stopPropagation) {
    event.stopPropagation();
  } else {
    event.cancelBubble = true;
  }
}

/**
 * preventDefault 浏览器阻止默认行为
 * @param  {Event} e
 */
export function preventDefault(e: Event): void {
  const event = e || window.event;
  if (event.preventDefault) {
    event.preventDefault();
  } else {
    event.returnValue = false;
  }
}

/**
 * 跨浏览器添加事件
 */
export function addEvent(
  obj: Window | Document | Element | any,
  type: string,
  fn: EventListenerOrEventListenerObject,
  options = false
) {
  if (obj.addEventListener) {
    obj.addEventListener(type, fn, options);
  } else if (obj.attachEvent) {
    //IE
    obj.attachEvent('on' + type, fn);
  }
  return fn;
}

//跨浏览器移除事件
export function removeEvent(
  obj: Window | Element | Document | any,
  type: string,
  fn: EventListenerOrEventListenerObject,
  options = false
) {
  if (obj.removeEventListener) {
    obj.removeEventListener(type, fn, options);
  } else if (obj.detachEvent) {
    //兼容IE
    obj.detachEvent('on' + type, fn);
  }
}

const getVisibilityChange = () => {
  let hidden = '',
    visibilityChange = '';
  if (!isUndefined(document.hidden)) {
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (!isUndefined(document.mozHidden)) {
    hidden = 'mozHidden';
    visibilityChange = 'mozvisibilitychange';
  } else if (!isUndefined(document.msHidden)) {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (!isUndefined(document.webkitHidden)) {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  }
  return {
    hidden,
    visibilityChange,
  };
};

// 暴露在外部使用的cookies
export enum addVisibilityCookies {
  isCheckSid = 'isCheckSid',
  isWait = 'isWait',
}

/**
 * H5 监听自身状态激活与失效
 * 1.App 内唤起原生登录页面，用户未登录直接关闭登录页，回到 H5 页面时，触发激活状态，执行自定义逻辑，退出 WebView
 * 2.站外 H5 唤起 App 开启定时器，在定时器等待过程中，App 被唤起，H5 页面处于失效状态瞬间，取消定时器，若一直未处于失效状态，则超时弹出提示：唤起 App 失败
 * @param {Function} onEnter 进入 H5 页面，触发激活状态
 * @param {Function} onLeave 离开 H5 页面，触发失效状态
 * @param {Param} options[isCheckSid] 是否需要激活的时候触发 验证Sid
 * @param {Param} options[isWait] 判断是否需要等待isCheckSid 再执行回调
 * @param {Param} options[immediate] 是否立即执行 激活方法
 * 三种情况
 * isCheckSid = true, isWait = true 调用，不需要等
 * isCheckSid = true, isWait = false 调用，需要等
 * isCheckSid = false, isWait = false 不调用，不需要等
 */

export const addVisibilityChange: any = function (
  onEnter: (data?: JsBridge.ICheckSID) => void,
  onLeave: () => void,
  options?: { isCheckSid?: boolean; isWait?: boolean; immediate?: boolean } & ICheckSidJsBridge
) {
  if (!isBrowser()) return;
  const { hidden, visibilityChange } = getVisibilityChange();
  const _options = {
    isCheckSid: true,
    isWait: true,
    immediate: true,
    ...options,
  };
  loggerInfo(['addVisibilityChange 参数', _options], LogModule.JSBridge);

  // 如果存在先注销一下
  addVisibilityChange?.func && removeVisibilityChange(addVisibilityChange.func);
  addVisibilityChange.func = async () => {
    const _isCheckSid = Boolean(getCookie(addVisibilityCookies.isCheckSid) || _options.isCheckSid);
    let _isWait = Boolean(getCookie(addVisibilityCookies.isWait) || _options.isWait);

    if (document[hidden]) {
      onLeave();
    } else {
      // 判断是否需要校验并设置SID
      if (_isCheckSid) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isCheckSid, ..._checkOpt } = _options;

        loggerInfo(['addVisibilityChange  checkSidJsBridge 参数', _checkOpt], LogModule.JSBridge);

        checkSidJsBridge(_checkOpt)
          .then(data => {
            _isWait && onEnter(data);
          })
          .catch(data => {
            _isWait && onEnter(data);
          });
      } else {
        _isWait = false;
      }

      !_isWait && onEnter();
    }
  };

  _options.immediate && addVisibilityChange.func();

  return addEvent(document, visibilityChange, addVisibilityChange.func);
};

export function removeVisibilityChange(removeListener: () => void) {
  if (!isBrowser()) return;
  const { visibilityChange } = getVisibilityChange();
  removeEvent(document, visibilityChange, removeListener);
}

/**
 * DOMContentLoaded 事件后做一些业务上的处理
 */
export const addDOMContentLoaded: any = function (onDOMContentLoaded?: () => void) {
  if (!isBrowser()) return;
  // 如果存在先注销一下
  addDOMContentLoaded?.func && removeDOMContentLoaded(addVisibilityChange.func);
  // 创建 DOMContentLoaded 监听回调事件
  addDOMContentLoaded.func = () => {
    // 尝试通知 App Web加载完毕
    inApp() && callHandlerToNative({ type: 'WEB_LOAD_FINISHED' });

    // 支持传入了自定义函数执行
    isFunction(onDOMContentLoaded) && onDOMContentLoaded();
  };
  // DOMContentLoaded 会等HTML文档加载完毕后触发，
  // 包括一些类似 vendor.js，manifest.js，app.js，动态加载 manifest 映射中的app-async.js等(项目webpack打包split chunk优化后的产物)
  // 不会等待样式表、图像和子框架完成加载
  return addEvent(document, 'DOMContentLoaded', addDOMContentLoaded.func);
};

export function removeDOMContentLoaded(removeListener: () => void) {
  if (!isBrowser()) return;
  removeEvent(window, 'DOMContentLoaded', removeListener);
}

/**
 * 静默预加载图片资源
 */
export function preloadImages(images: Array<string> | string) {
  if (!isBrowser()) return;
  if (!isArray(images) && !isString(images)) return;
  if (isString(images)) images = [images];
  if (!images.length) return;
  const onload = async () => {
    let cacheImages = storage.session.get(HsUtilPreloadImages);
    cacheImages = (isArray(cacheImages) ? cacheImages : []).filter((item: any) => isString(item));
    cacheImages = Array.from(new Set(cacheImages));
    const queue: Array<Promise<any>> = [];
    (images as Array<string>).forEach(url => {
      const isExist = cacheImages.find((item: string) => item === url);
      if (!isExist) {
        const imageElem = new Image();
        imageElem.src = url;
        queue.push(
          new Promise(resolve => {
            imageElem.onload = () => {
              resolve({ status: 'load', url });
            };
            imageElem.onerror = () => {
              resolve({ status: 'error', url });
            };
          })
        );
      }
    });
    cacheImages = (await Promise.all(queue))
      .filter(item => item.status === 'load')
      .map(item => item.url);
    cacheImages = Array.from(new Set(cacheImages));
    storage.session.set(HsUtilPreloadImages, cacheImages);
  };
  // 判断页面是否加载完成
  if (document.readyState === 'complete') {
    onload();
  } else {
    addEvent(window, 'load', onload, false);
  }
}

/**
 * 富文本内容冒泡拦截
 */
export function htmlSpreadIntercept(opts: IHtmlSpreadInterceptOpts = {}) {
  const { el, rule, cb } = { ...opts } as IHtmlSpreadInterceptOpts;
  let ele = null;
  let elMatch: THtmlSpreadInterceptRule = null;
  // 检查节点
  if ((el as Element)?.querySelector) ele = el;
  else if (isString(el)) ele = document.querySelector(el);
  if (isInvalid(ele)) return;

  // 检查过滤规则
  if (isInvalid(rule)) return;
  if (isFunction(rule)) elMatch = rule;
  else
    elMatch = (opts = {}) => {
      const { el } = { ...opts } as IHtmlSpreadInterceptOpts;
      if (isInvalid(el)) return false;
      let r: Array<any> = [];
      const rMap: Record<string, any> = {};
      const { nodeName } = el as Element;
      if (isString(rule)) r = rule.split(',');
      else if (isArray(rule)) {
        rule.forEach(item => {
          if (isString(item)) r.push(item);
          if (isObject(item, true)) {
            const { r: _r, isStop = true, isDefault = true } = item;
            if (isString(_r)) {
              r.push(_r);
              rMap[_r] = { isStop, isDefault };
            }
          }
        });
      } else r = new String(rule).toString().split(',');
      r = r.map(item => item.toUpperCase());
      return {
        isStop: true,
        isDefault: true,
        isMatch: r.includes(nodeName),
        ...(isObject(rMap[nodeName], true) ? rMap[nodeName] : {}),
      };
    };

  addEvent(ele, 'click', ev => {
    const elTarget = ev.target;
    if (elTarget) {
      if (isFunction(elMatch)) {
        const { isMatch, isStop, isDefault } = elMatch({ el: elTarget }) as Record<string, any>;
        if (!isMatch) return;
        if (isStop) stopPropagation(ev); // 阻止冒泡
        if (isDefault) preventDefault(ev); // 阻止默认行为
        isFunction(cb) && cb({ el: elTarget });
      }
    }
  });
}

/**
 * 滚动到指定位置，兼容替代 scrollIntoView
 * @param {Object} options 参数 {targetEl: '目标元素', targetTop: ‘目标高度’, scrollEl: '滚动容器', speed: '滚动速度', deviation: '滚动偏移量'}
 */
export function scrollToViewAnimate(
  options: IScrollToViewAnimateOptions = {
    lockScrollEl: false, // 是否锁定采用 scrollEl 滚动容器的滚动高度
    speed: 50, // 滚动速度
    deviation: 0, // 滚动偏移量
    isAnimate: true, // 是否开启动画
  }
) {
  if (!isBrowser()) return;
  let scrollEl: Element | Element[] | undefined = options?.scrollEl || document.body;
  const lockScrollEl = options.lockScrollEl;
  if (lockScrollEl) scrollEl = options?.scrollEl;
  if (!scrollEl) return;
  //获取元素相对窗口的top值，此处应加上窗口本身的偏移
  const scrollTop = lockScrollEl
    ? scrollEl.scrollTop
    : scrollEl.scrollTop ||
      document.body.scrollTop ||
      document.documentElement.scrollTop ||
      window.pageYOffset;
  if (scrollEl.nodeName === 'BODY' && !document.body.scrollTop && scrollTop) {
    // 滚动距离存在，但 body 的滚动高度为零，则表示滚动容器不是 body
    scrollEl = document.documentElement;
  }
  if (scrollEl.nodeName === 'BODY' && !scrollTop) {
    // 当前滚动高度不存在，无法判断滚动容器是 body 还是 html，因此两者都参与执行
    scrollEl = [scrollEl, document.documentElement];
  }
  let requestId;
  let isEnd = false;
  let currentTop = scrollTop;
  let deviation = options?.deviation || 0;
  const speed = options?.speed || 50;

  let rect: any;
  let nextTop = 0;

  if (!isInvalid(options?.targetEl)) {
    rect = options.targetEl?.getBoundingClientRect();
    nextTop = scrollTop + rect.top;
  } else nextTop = options.targetTop || scrollTop;

  const direction = nextTop >= scrollTop ? 'up' : 'down';

  // 根据 fonSize 计算偏移量的差量
  const fontSizeEl = document.querySelector(options?.fontSizeEl || 'html') as Element;
  const styles = window.getComputedStyle(fontSizeEl, null);
  const webSize = options?.webSize || 37.5;
  const webSizeRate = trimCssUnit(calcCssUnit(deviation, { root: webSize }));
  deviation = trimCssUnit(styles.fontSize) * webSizeRate;

  // 滚动到指定位置
  function scrollToTarget({ targetTop }: { targetTop: number }) {
    if (isInvalid(targetTop)) return;
    if (isArray(scrollEl)) scrollEl.forEach(el => el.scrollTo(0, targetTop));
    else scrollEl!.scrollTo(0, targetTop);
  }

  //采用requestAnimationFrame，平滑动画
  function step() {
    if (direction === 'up') {
      currentTop += speed;
      if (currentTop > nextTop) {
        currentTop = nextTop + deviation;
        isEnd = true;
      }
    } else {
      currentTop -= speed;
      if (currentTop < nextTop) {
        currentTop = nextTop + deviation;
        isEnd = true;
      }
    }
    scrollToTarget({ targetTop: currentTop });
    requestId = window.requestAnimationFrame(step);
    isEnd && window.cancelAnimationFrame(requestId);
  }
  if (options?.isAnimate) window.requestAnimationFrame(step);
  else scrollToTarget({ targetTop: nextTop });
}

export function initCompatibleStyleForHtmlContent<E extends Element = Element>(
  rootElement?: E | Document,
  opts: Record<string, any> = {}
) {
  if (!isBrowser()) return;
  if (!rootElement) {
    rootElement = document;
  }
  // 每个展业兼容处理可能有所不同，因此按展业区分。
  const { platform } = { platform: getUtilsConfig().platform, ...opts };
  if (platform === Platform.SA) {
    /**
     * 以下给爬虫抓来的文章的table标签，套一层标签，为了实现表格水平宽度大于屏幕，支持水平滚动；
     */
    const tables = rootElement.querySelectorAll('table');
    tables.forEach(element => {
      if (element.parentElement) {
        const parent = element.parentElement;
        const isRootElement = Array.from(parent.classList).includes('hs-editor');
        const isPresetTable =
          parent.nodeType === 1 &&
          parent.tagName === 'FIGURE' &&
          Array.from(parent.classList).includes('table');
        if (isRootElement) {
          const figureElement = document.createElement('figure');
          figureElement.classList.add('table');
          figureElement.appendChild(element.cloneNode(true));
          parent.replaceChild(figureElement, element);
        } else if (!isRootElement && !isPresetTable) {
          element.parentElement.style.overflowX = 'auto';
          // 未知原因，部分表格在滚动容器内表格滚不到最左边的样子, 导致边框看不见
          element.parentElement.style.paddingInlineEnd = '1px';
        }
      }
    });
  }
}
